import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { t } from "../../i18n/translations";
import { Pencil, Trash2, Plus, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const emptyService = {
  slug: "", duration_minutes: 60, price_chf: 0, image_url: "", active: true, order_idx: 0,
  name: { de: "", fr: "", it: "", en: "" },
  short_description: { de: "", fr: "", it: "", en: "" },
  description: { de: "", fr: "", it: "", en: "" },
};

export default function AdminServices() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // service object | null

  const load = async () => { const { data } = await api.get("/admin/services"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/admin/services/${editing.id}`, editing);
      } else {
        await api.post(`/admin/services`, editing);
      }
      toast.success("✓");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("?")) return;
    await api.delete(`/admin/services/${id}`);
    load();
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte ein Bild wählen (JPG/PNG/WebP)");
      return null;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Bild zu gross (max 8 MB)");
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return r.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload fehlgeschlagen");
      return null;
    }
  };

  // Direct upload from card (without opening editor)
  const uploadForCard = async (service, file) => {
    const url = await uploadImage(file);
    if (!url) return;
    try {
      await api.put(`/admin/services/${service.id}`, { ...service, image_url: url });
      toast.success("Bild aktualisiert");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  return (
    <div className="p-5 sm:p-8 lg:p-10" data-testid="admin-services-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl text-ink">{t(lang, "admin.services")}</h1>
        <button onClick={() => setEditing({ ...emptyService })} className="inline-flex items-center gap-2 px-5 py-2.5 bg-terracotta text-white rounded-full hover:bg-terracotta-hover" data-testid="services-add">
          <Plus className="w-4 h-4" strokeWidth={1.5}/> {t(lang, "admin.add_new")}
        </button>
      </div>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-border rounded-md overflow-hidden" data-testid={`service-item-${s.slug}`}>
            <label className="relative aspect-[16/10] bg-muted overflow-hidden block group cursor-pointer" title="Bild hochladen oder ersetzen">
              {s.image_url ? (
                <img src={s.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-ink-muted">
                  <ImageIcon className="w-8 h-8 mb-1" strokeWidth={1.2} />
                  <span className="text-xs">Kein Bild</span>
                </div>
              )}
              {/* Overlay – always clickable, more visible when no image */}
              <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${s.image_url ? "bg-ink/0 opacity-0 group-hover:opacity-100 group-hover:bg-ink/50" : "bg-ink/10 opacity-100 group-hover:bg-ink/40"}`}>
                <div className="px-3 py-2 bg-white rounded-full text-xs font-medium text-ink inline-flex items-center gap-1.5 shadow">
                  <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> {s.image_url ? "Bild ersetzen" : "Bild hochladen"}
                </div>
              </div>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadForCard(s, f); e.target.value = ""; }}
                data-testid={`service-upload-${s.slug}`}
              />
            </label>
            <div className="p-5">
              <div className="font-display text-xl text-ink leading-tight">{s.name?.[lang] || s.name?.de}</div>
              <div className="text-xs text-ink-muted mt-1">{s.duration_minutes} min · CHF {s.price_chf} · #{s.order_idx}</div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing({ ...s })} className="px-3 py-2 text-xs border border-border rounded-md hover:bg-muted inline-flex items-center gap-1.5" data-testid={`service-edit-${s.slug}`}><Pencil className="w-3.5 h-3.5" strokeWidth={1.5}/>{t(lang, "admin.edit")}</button>
                <button onClick={() => del(s.id)} className="px-3 py-2 text-xs border border-border rounded-md hover:bg-destructive/10 text-destructive inline-flex items-center gap-1.5" data-testid={`service-del-${s.slug}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>{t(lang, "admin.delete")}</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/60 z-50 flex items-center justify-center p-4 overflow-auto" onClick={(e) => e.target === e.currentTarget && setEditing(null)} data-testid="service-editor">
          <div className="bg-white rounded-md max-w-3xl w-full p-8 my-10">
            <h2 className="font-display text-3xl">{editing.id ? t(lang, "admin.edit") : t(lang, "admin.add_new")}</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Slug" value={editing.slug} onChange={(v) => setEditing({...editing, slug: v})} testid="svc-slug" />
              <Field label="Order" type="number" value={editing.order_idx} onChange={(v) => setEditing({...editing, order_idx: parseInt(v) || 0})} testid="svc-order" />
              <Field label="Duration (min)" type="number" value={editing.duration_minutes} onChange={(v) => setEditing({...editing, duration_minutes: parseInt(v) || 0})} testid="svc-duration" />
              <Field label="Price CHF" type="number" value={editing.price_chf} onChange={(v) => setEditing({...editing, price_chf: parseFloat(v) || 0})} testid="svc-price" />
              <div className="md:col-span-2">
                <span className="text-xs uppercase tracking-widest text-ink-muted">Bild</span>
                <div className="mt-2 flex flex-col sm:flex-row gap-3">
                  {editing.image_url ? (
                    <img src={editing.image_url} alt="" className="w-32 h-20 object-cover rounded border border-border flex-shrink-0" />
                  ) : (
                    <div className="w-32 h-20 rounded border border-dashed border-border bg-muted flex items-center justify-center text-ink-muted flex-shrink-0">
                      <ImageIcon className="w-6 h-6" strokeWidth={1.2} />
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-terracotta text-white rounded-md cursor-pointer hover:bg-terracotta-hover text-sm">
                      <Upload className="w-4 h-4" strokeWidth={1.5} /> Bild hochladen (JPG/PNG/WebP · max 8 MB)
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const url = await uploadImage(f);
                          if (url) setEditing({ ...editing, image_url: url });
                          e.target.value = "";
                        }}
                        data-testid="svc-image-upload"
                      />
                    </label>
                    <input
                      type="text"
                      value={editing.image_url || ""}
                      onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                      placeholder="Oder URL direkt eingeben"
                      className="w-full px-3 py-2 border border-border rounded-md text-sm"
                      data-testid="svc-image"
                    />
                    {editing.image_url && (
                      <button
                        type="button"
                        onClick={() => setEditing({ ...editing, image_url: "" })}
                        className="text-xs text-destructive hover:underline"
                      >
                        Bild entfernen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {["de", "fr", "it", "en"].map((l) => (
                <Field key={`n-${l}`} label={`Name (${l.toUpperCase()})`} value={editing.name?.[l] || ""} onChange={(v) => setEditing({...editing, name: { ...editing.name, [l]: v }})} testid={`svc-name-${l}`} />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              {["de", "fr", "it", "en"].map((l) => (
                <Field key={`sd-${l}`} label={`Short description (${l.toUpperCase()})`} value={editing.short_description?.[l] || ""} onChange={(v) => setEditing({...editing, short_description: { ...editing.short_description, [l]: v }})} testid={`svc-short-${l}`} />
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3">
              {["de", "fr", "it", "en"].map((l) => (
                <Field key={`dd-${l}`} label={`Description (${l.toUpperCase()})`} value={editing.description?.[l] || ""} onChange={(v) => setEditing({...editing, description: { ...editing.description, [l]: v }})} textarea testid={`svc-desc-${l}`} />
              ))}
            </div>
            <label className="mt-4 inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({...editing, active: e.target.checked})} data-testid="svc-active" /> Active
            </label>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 border border-border rounded-md">{t(lang, "admin.cancel")}</button>
              <button onClick={save} className="px-5 py-2.5 bg-terracotta text-white rounded-md" data-testid="svc-save">{t(lang, "admin.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", textarea = false, testid }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-ink-muted">{label}</span>
    {textarea ? (
      <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="mt-2 w-full px-3 py-2 border border-border rounded-md" />
    ) : (
      <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 w-full px-3 py-2 border border-border rounded-md" />
    )}
  </label>
);
